import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import horizontalScroll from 'webapp/modifiers/horizontal-scroll';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DesignerTopbarDataboardSelectListItemComponent from 'webapp/components/designer/topbar/databoard-select-list-item';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import FilterSortMixedInDesigner, {
	ViewType,
} from 'webapp/models-custom/filter-sort/mixed-in-designer';

interface Args {
	selectedBoard: Board | null;
	selectedLoop: BoardLoop | null;
	onChange: (board: Board | BoardLoop) => void;
	onCreateNew: (viewType: ViewType) => void;
	onClose: () => void;
}

export default class DesignerTopbarDataboardSelectListComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;

	filterSort: FilterSortMixedInDesigner;
	elementHeight = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// load view settings
		let viewId = 'databoard';

		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.viewSettingsName, true) ||
			{};
		if (savedValues.viewId) {
			viewId = savedValues.viewId;
		}

		const filterSort = new FilterSortMixedInDesigner(
			{
				getAllItems: () => {
					const currentViewId = this.filterSort?.viewId || viewId;
					if (currentViewId === ViewType.BOARD) {
						return this.mmBoardsService.normalBoardModels;
					} else if (currentViewId === ViewType.ACCOUNT_TEMPLATE) {
						return this.mmBoardsService.accountTemplateBoardModels.filter(
							(item) => item.canCurrentUserEdit,
						);
					} else if (currentViewId === ViewType.LOOP) {
						return this.mmBoardLoopsService.normalBoardLoopModels.filter(
							(item) => item.canCurrentUserEdit,
						);
					} else {
						return [];
					}
				},
				onOpenView: (viewId) => {
					this.filterSort.viewId = viewId;
					this.viewSettingsManagerService.save(this.viewSettingsName, { viewId }, true);
					this.toggleFilters();
				},
			},
			viewId,
		);

		this.filterSort = filterSort;

		next(this, this.toggleFilters);
	}

	get viewSettingsName(): string {
		return `databoardSelectListViewSettings-${this.accountService.id}`;
	}

	get selected(): Board | BoardLoop | null {
		if (this.filterSort.viewId === ViewType.LOOP) {
			return this.args.selectedLoop;
		} else {
			return this.args.selectedBoard;
		}
	}

	toggleFilters(): void {
		const viewId = this.filterSort.viewId;

		// hide category filter for loops and templates
		const filterCategory = this.filterSort.findFilter(1);
		if (filterCategory) {
			filterCategory.manualHide = viewId !== ViewType.BOARD;
		}

		// hide data source filter for loops
		const filterDatasource = this.filterSort.findFilter(3);
		if (filterDatasource) {
			filterDatasource.manualHide = viewId === ViewType.LOOP;
		}

		// hide created by filter for loops and databoards
		const filterCreator = this.filterSort.findFilter(4);
		if (filterCreator) {
			filterCreator.manualHide = viewId !== ViewType.ACCOUNT_TEMPLATE;
		}
	}

	autoCloseElement(e: PointerEvent): void {
		const target = e.target as HTMLElement;
		if (
			isAlive(this) &&
			target &&
			!target.closest('.optionspopup') && // don't close on open filter popups
			e.clientY > window.innerHeight / 2 &&
			e.clientY > this.elementHeight + 60 // to add some extra space to get out of element with mouse - if window height is really small
		) {
			this.args.onClose();
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	createNew(): void {
		this.args.onCreateNew(this.filterSort.viewId as ViewType);
	}

	@action
	debounceAutoCloseElement(e: Event): void {
		debounce(this, this.autoCloseElement, e as PointerEvent, 100);
	}

	@action
	scrollToActiveBoard(node: HTMLElement): void {
		node.querySelector('ul .board.selected')?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});
	}

	@action
	setupView(node: HTMLElement): void {
		const topbarNode = document.getElementById('topbar'),
			elementHeight = node.offsetHeight + (topbarNode?.offsetHeight || 0);
		this.elementHeight = elementHeight;

		// close when mouse is over half of window's height
		window.addEventListener('mousemove', this.debounceAutoCloseElement);

		scheduleOnce('afterRender', this, this.scrollToActiveBoard, node);
	}

	@action
	teardownView(): void {
		window.removeEventListener('mousemove', this.debounceAutoCloseElement);
	}
	<template>
		<div
			class="databoard-select-list"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<a role="button" class="close" {{on "click" @onClose}}>
				<CommonUiIconComponent @name="Close" @size="lg" />
			</a>
			<div class="content">

				<FilterLineProxyComponent @model={{this.filterSort}} @theme="dark" />

				<ul
					{{horizontalScroll}}
					{{visibleCheckContainer true}}
					{{didUpdate this.triggerContentChange this.filterSort.results}}
				>
					{{#each this.filterSort.results as |item|}}
						<DesignerTopbarDataboardSelectListItemComponent
							@model={{item}}
							@selected={{this.selected}}
							@onChange={{@onChange}}
						/>
					{{/each}}
					<li class="placeholder">
						<a role="button" {{on "click" this.createNew}}>
							<div class="board">
								<span>+</span>
							</div>
						</a>
						<div class="board-name">New</div>
					</li>
				</ul>
			</div>
		</div>
	</template>
}
